import { isAxiosError } from 'axios';
import { Dispatch, AnyAction } from 'redux';
import { jwtDecode } from 'jwt-decode';

import { CompanyDevicePolicyStatus, ForbiddenErrorErrorEnum, UserOrganizationResponse, UserRole } from '@cdaas/api-clients/dist/asset-user-openapi';

import { FeatureFlags, API_ERROR_AUTH_FAILED } from '../../utils/constants';
import * as actionTypes from './actionTypes';
import { AuthActions, Credentials, TokenPayload, User } from '../../types';
import { RootState } from '../reducers';
import { AppThunk } from './';
import history from './../../history';
import {
  getCredentials,
  getNumberOfPendingApprovals,
  getStorageItem,
  getUserInfo,
  hasUserARequiredRole,
  redirectToUrlInCurrentTab,
  removeStorageItems,
  requestLogout,
  resetAuthenticationHeader,
  resetUnauthenticationInterceptor,
  setAuthenticationHeader,
  setStorageItems,
  setUnauthenticationInterceptor,
  typeGuards,
} from '../../utils';

export const authFail = (error: null | string): AuthActions => ({
  type: actionTypes.AUTH_FAIL,
  error,
});

export const authLogout = (): AuthActions => {
  removeStorageItems(['tCredentials'], 'local');
  removeStorageItems(['tItem', 'tDelivery', 'tSelectedDelivery', 'tOrderResp', 'tSelectedOrganization'], 'session');
  resetAuthenticationHeader();
  resetUnauthenticationInterceptor();
  return {
    type: actionTypes.AUTH_LOGOUT,
  };
};

export const authSetCredentials = (credentials: Credentials, mode: 'setToken' | 'refreshToken'): AuthActions => {
  if (mode === 'setToken') {
    removeStorageItems(['tState'], 'local');
  }
  setAuthenticationHeader(credentials.accessToken);
  return {
    type: actionTypes.AUTH_SET_CREDENTIALS,
    credentials,
  };
};

export const authSetPendingApprovals = (pendingApprovals: number | null): AuthActions => ({
  type: actionTypes.AUTH_SET_PENDING_APPROVALS,
  pendingApprovals,
});

export const authSetRequestedPath = (path: string | null): AuthActions => ({
  type: actionTypes.AUTH_SET_REQUESTED_PATH,
  path,
});

export const authSetSelectedOrganization = (selectedOrganization: UserOrganizationResponse | null, redirectToHome?: boolean): AuthActions => {
  setStorageItems(['tSelectedOrganization'], [selectedOrganization], 'session');
  if (redirectToHome) {
    history.push('/home');
  }

  return {
    type: actionTypes.AUTH_SET_SELECTED_ORGANIZATION,
    selectedOrganization,
  };
};

export const authSetUserInfo = (user: User): AuthActions => ({
  type: actionTypes.AUTH_SET_USER_INFO,
  user,
});

export const authStart = (): AuthActions => {
  return {
    type: actionTypes.AUTH_START,
  };
};

export const authSuccess = (user: User): AuthActions => {
  setUnauthenticationInterceptor();
  return {
    type: actionTypes.AUTH_SUCCESS,
    user,
  };
};

export const bankIdNotVerified = (): AuthActions => ({
  type: actionTypes.AUTH_BANK_ID_NOT_VERIFIED,
});

export const clearAuthError = (): AuthActions => ({
  type: actionTypes.AUTH_ERROR_CLEAR,
});

export const executeLogout = (): AppThunk<Promise<void>> => async (dispatch: Dispatch) => {
  await requestLogout();
  dispatch(authLogout());
  const logoutUri = new URL(process.env.REACT_APP_AUTH_CIAM_LOGOUT_URI!);
  logoutUri.searchParams.append('post_logout_redirect_uri', process.env.REACT_APP_AUTH_LOGOUT_RETURN_LINK!);
  redirectToUrlInCurrentTab(logoutUri.toString());
};

export const getPendingApprovals = (): AppThunk<Promise<void>> => async (dispatch: Dispatch) => {
  if (hasUserARequiredRole([UserRole.Approver])) {
    const pendingApprovals = await getNumberOfPendingApprovals();
    if (pendingApprovals) {
      dispatch(authSetPendingApprovals(pendingApprovals));
    }
  }
};

export const phoneNumberNotVerified = (phoneNumber: string, path: string): AuthActions => ({
  type: actionTypes.AUTH_PHONE_NUMBER_NOT_VERIFIED,
  phoneNumber,
  path,
});

export const phoneNumberVerified = (): AuthActions => ({
  type: actionTypes.AUTH_PHONE_NUMBER_VERIFIED,
});

export const setAutoAuthFail = (): AuthActions => ({
  type: actionTypes.AUTH_SET_AUTO_FAIL,
});

const checkAndRedirectToCompanyDevicePolicy = (companyDevicePolicy?: CompanyDevicePolicyStatus) => {
  if (companyDevicePolicy === CompanyDevicePolicyStatus.NotAccepted) {
    history.push('/authentication/company-device-policy');
    return false;
  }
  return true;
};

const parseCiamTargetAcrValue = (acr?: string): number => {
  switch (acr) {
    case 'urn:teliacompany:dis:ciam:aal:gold':
      return 3; // BankID
    case 'urn:teliacompany:dis:ciam:aal:silver':
      return 2; // multi-factor authentication
    case 'urn:teliacompany:dis:ciam:aal:bronze':
      return 1; // single-factor authentication
    default:
      return Number(acr); // Fallback to numeric acr value
  }
};

/**
 * If MFA is enabled, verify token has an acr level of 2 or higher (=2FA or BankID auth).
 * If acr check fails, redirect to login page which should display only login button (?existingUser=true query param).
 * @param credentials User credentials
 * @param featureFlags
 * @returns true if MFA is enabled & acr check fails, false if acr value is OK or MFA is disabled
 */
const checkTokenAcrLevelAndRedirectToLogin = (credentials: Credentials, featureFlags: FeatureFlags) => {
  const isMfaEnabled = featureFlags.FEATURE_FLAG_CIAM_MFA_ENABLED;
  if (isMfaEnabled) {
    const { acr } = jwtDecode<TokenPayload>(credentials.accessToken);
    const parsedAcr = parseCiamTargetAcrValue(acr);
    if (isNaN(parsedAcr) || parsedAcr < 2) {
      history.push('/authentication/login?existingUser=true');
      return true;
    }
  }
  return false;
};

const failLogout = (dispatch: Dispatch, options?: { dispatchAutoAuthFail?: boolean; errorMessage?: string }) => {
  if (options?.dispatchAutoAuthFail) dispatch(setAutoAuthFail());
  dispatch(authLogout());
  dispatch(authFail(options?.errorMessage ?? 'Auth failed'));
};

export const getLoggedInUser = (): AppThunk<Promise<void>> => async (dispatch: Dispatch, getState: () => RootState) => {
  const user = await getUserInfo();
  if (user && !isAxiosError(user)) {
    dispatch(authSuccess(user));
    const continueFlow = checkAndRedirectToCompanyDevicePolicy(user.policies?.company_device_policy);
    if (!continueFlow) return;
    const path = getState().authentication.phoneNumberVerification.path;
    history.push(!path || path === '/authentication/logout' ? '/home' : path);
  } else {
    failLogout(dispatch);
  }
};

export const authenticate =
  (code: string, redirectUri: string, path: string): AppThunk<Promise<void>> =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(authStart());

    const credentials = await getCredentials(code, redirectUri);
    if (!credentials) {
      failLogout(dispatch, { errorMessage: API_ERROR_AUTH_FAILED.errorMessage });
      globalThis.location.href = `${process.env.REACT_APP_AUTH_CIAM_LOGOUT_URI}?redirect_uri=${process.env.REACT_APP_AUTH_LOGOUT_AFTER_LOGIN_FAILED_RETURN_LINK}`;
      return;
    }

    if (checkTokenAcrLevelAndRedirectToLogin(credentials, getState().featureFlags)) {
      return;
    }

    setStorageItems(['tCredentials'], [credentials], 'local');
    dispatch(authSetCredentials(credentials, 'setToken'));

    const user = await getUserInfo();

    if (!user || isAxiosError(user)) {
      if (typeGuards.isPhoneNumberNotVerifiedForbiddenErrorResponse(user?.response)) {
        dispatch(phoneNumberNotVerified(user.response.data.phone_number, path));
      } else if (typeGuards.isForbiddenErrorResponse(user?.response) && user.response.data.error === ForbiddenErrorErrorEnum.IdentityNotVerified) {
        dispatch(bankIdNotVerified());
      } else if (
        typeGuards.isForbiddenErrorResponse(user?.response) &&
        user.response.data.error === ForbiddenErrorErrorEnum.PrescheduledOnboardingInFuture
      ) {
        failLogout(dispatch, { errorMessage: ForbiddenErrorErrorEnum.PrescheduledOnboardingInFuture });
      } else if (typeGuards.isForbiddenErrorResponse(user?.response)) {
        failLogout(dispatch, { errorMessage: ForbiddenErrorErrorEnum.Forbidden });
      } else {
        failLogout(dispatch, { errorMessage: API_ERROR_AUTH_FAILED.errorMessage });
      }
      return;
    }

    dispatch(authSuccess(user));

    const continueFlow = checkAndRedirectToCompanyDevicePolicy(user.policies?.company_device_policy);
    if (!continueFlow) return;

    const newPath =
      !path || path === '/authentication/logout' || path === '/authentication/authenticated' || path === '/authentication/login' ? '/home' : path;
    history.push(newPath);
  };

export const tryAutoAuth = (): AppThunk<Promise<void>> => async (dispatch: Dispatch, getState: () => RootState) => {
  const credentials = getStorageItem<Credentials>('tCredentials', 'local', true);
  if (!credentials) {
    failLogout(dispatch, { dispatchAutoAuthFail: true });
    return;
  }

  credentials.expiryDate = new Date(credentials.expiryDate);
  if (credentials.expiryDate.getTime() <= Date.now()) {
    removeStorageItems(['tCredentials'], 'local');
    failLogout(dispatch, { dispatchAutoAuthFail: true });
    return;
  }

  dispatch(authSetCredentials(credentials, 'setToken'));

  if (checkTokenAcrLevelAndRedirectToLogin(credentials, getState().featureFlags)) {
    return;
  }

  const user = await getUserInfo();
  if (!user || isAxiosError(user)) {
    history.push('/authentication/logout');
    dispatch(executeLogout() as unknown as AnyAction);
    return;
  }

  dispatch(authSuccess(user));

  checkAndRedirectToCompanyDevicePolicy(user.policies?.company_device_policy);
};

export const refreshUserInfo = (): AppThunk<Promise<void>> => async (dispatch: Dispatch) => {
  const user = await getUserInfo();
  if (!user || isAxiosError(user)) {
    return;
  }

  dispatch(authSetUserInfo(user));

  checkAndRedirectToCompanyDevicePolicy(user.policies?.company_device_policy);
};
